/**
 * Aero Figma source editor: the full Figmafolio viewport experience (2 to 6
 * breakpoints, per-viewport link, editable name and width, inheritance,
 * validation) wired to save onto an Aero url doc.
 *
 * It writes the same fields FolioForm.saveFigmaUrl writes for a folio
 * (viewports + the legacy urls map), each link transformed once through
 * editFigmaUrl so the stored value is render-ready, plus type 'figma' and the
 * aeroSite flag so the doc stays in Aero Sites. The save merges, so it never
 * disturbs any html data the doc also holds.
 *
 * Used both as the Settings "Figma prototype" card (edit later) and as the
 * setup surface for a freshly created Figma site that has no link yet.
 */
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import firebase from '../../firebase';
import { errorLine } from '../../components/Aero/AeroStatePanel';
import ViewportEditor from '../../components/ViewportEditor/ViewportEditor';
import FigmaShareHint from '../../components/Aero/FigmaShareHint';
import { recordCreationSource } from '../../utils/creationSource';
import { editFigmaUrl } from '../../utils/figmaEmbedUrl';
import {
  migrateToViewports,
  getDefaultViewports,
  viewportsToLegacyFormat,
  isWrongUrlType,
} from '../../utils/viewportUtils';

function AeroFigmaSourceEditor({ user, ownerUid, site, fetchSite, fetchVersions, onSaved }) {
  // Shared sites write to the OWNER's path, not the logged-in admin's.
  const writeOwnerUid = ownerUid || user.uid;
  const siteRef = firebase
    .firestore()
    .collection('user').doc(writeOwnerUid)
    .collection('url').doc(site.id);

  // Seed from the doc's stored viewports (migrating the legacy urls map when
  // present), or the default Mobile/Desktop split for a brand-new Figma site.
  const [viewports, setViewports] = useState(() => {
    const migrated = migrateToViewports(site);
    return migrated.viewports && migrated.viewports.length
      ? migrated.viewports
      : getDefaultViewports();
  });
  const [saving, setSaving] = useState(false);
  // Did the doc already hold a prototype link when this editor opened?
  const hadLink = (migrateToViewports(site).viewports || []).some((v) => v.figmaUrl);

  // Same gate FolioForm.saveFigmaUrl uses: at least one prototype/embed link,
  // and no file/design links anywhere.
  const hasValidUrl = viewports.some(
    (v) => v.figmaUrl && (v.figmaUrl.includes('figma.com/proto') || v.figmaUrl.includes('figma.com/embed'))
  );
  const hasFileUrl = viewports.some((v) => isWrongUrlType(v.figmaUrl));
  const canSave = hasValidUrl && !hasFileUrl;

  const handleSave = async () => {
    if (saving || !canSave) return;
    setSaving(true);
    try {
      const processed = viewports.map((v) => ({
        ...v,
        figmaUrl: v.figmaUrl ? editFigmaUrl(v.figmaUrl) : null,
      }));
      await siteRef.set(
        {
          userId: writeOwnerUid,
          type: 'figma',
          aeroSite: true,
          isDraft: 'false',
          // Embedding a prototype makes the site live, so clear any
          // creation-draft flag (no-op for sites that never had one).
          draft: firebase.firestore.FieldValue.delete(),
          viewports: processed,
          urls: viewportsToLegacyFormat(processed),
          updatedAt: new Date(),
        },
        { merge: true }
      );
      // Count only the first valid link on this doc (not every re-save).
      if (!hadLink) recordCreationSource('figma_embed', 'aero');
      setViewports(processed);
      await fetchSite();
      if (fetchVersions) await fetchVersions();
      toast.success('Figma prototype saved.');
      if (onSaved) onSaved();
    } catch (err) {
      console.error('Error saving Figma prototype:', err);
      toast.error('Could not save the prototype. ' + errorLine(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="aero-card aero-card-pad">
      <div className="aero-section-label">Figma prototype</div>
      <p className="aero-sites-field-note" style={{ margin: '0 0 4px' }}>
        Add a prototype link for each screen size. Visitors get the right one
        for their device; empty sizes inherit from the next larger one.
      </p>
      <FigmaShareHint className="aero-sites-field-note" withPassword />
      <ViewportEditor
        variant="aero"
        viewports={viewports}
        onViewportsChange={setViewports}
        linkCheck
      />
      {!canSave && (hasFileUrl || hasValidUrl === false) && (
        <p className="aero-sites-field-warning" style={{ marginBottom: 12 }}>
          Add at least one prototype link (figma.com/proto/...). File and design
          links can't be embedded.
        </p>
      )}
      <div className="aero-sites-field-row" style={{ justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="aero-btn aero-btn-primary"
          onClick={handleSave}
          disabled={saving || !canSave}
        >
          {saving ? 'Saving…' : 'Save prototype'}
        </button>
      </div>
    </div>
  );
}

export default AeroFigmaSourceEditor;
